<template>
  <button
    v-wave
    v-tooltip="{
      content: item.tooltip,
      placement: Placement.Bottom,
      distance: 8,
      delay: { show: 300, hide: 100 },
    }"
    :disabled="item.disabled"
    :data-external="`dsr-navigation-item-${item.key}`"
    :class="['button', { active, disabled: item.disabled }]"
    @click="$emit('click')"
  >
    <FriedIcon v-if="item.icon" :icon="item.icon" class="icon" />
    <span v-if="isSmallScreenAndDown" class="button-text">
      <FriedH4>{{ item.label }}</FriedH4>
    </span>
    <span v-else class="button-text">
      {{ item.label }}
    </span>
    <slot name="notification" />
  </button>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import type { IconName } from '@getaccept/fried-tofu';
import { Placement } from '@getaccept/fried-tofu';
import type { DrView } from '../types';

export default defineComponent({
  name: 'NavigationItem',
  props: {
    active: {
      type: Boolean,
    },
    item: {
      type: Object as PropType<{
        label: string;
        key: DrView;
        disabled?: boolean;
        icon?: IconName;
        tooltip?: string;
      }>,
      required: true,
    },
  },
  emits: ['click'],
  setup() {
    const { isSmallScreenAndDown } = useBreakpoints();

    return {
      isSmallScreenAndDown,
      Placement,
    };
  },
});
</script>
<style lang="scss" scoped>
.button {
  font-family: var(--default-font-family);
  font-size: var(--paragraph-large-font-size);
  line-height: var(--paragraph-large-line-height);
  margin-block-start: 0;
  margin-block-end: 0;
  border-radius: var(--radius-medium);
  font-weight: 600;
  min-height: 2.5rem;
  cursor: pointer;
  padding: var(--spacing-50) var(--spacing-75);
  transition: background-color 0.1s ease-in-out;
  position: relative;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  background-color: transparent;
  color: var(--text-gray);
  margin-right: var(--spacing-25);

  @media screen and (max-width: $sm) {
    padding: var(--spacing-50);
    width: 100%;
  }

  h4 {
    font-size: var(--paragraph-large-font-size);
  }

  &:hover,
  &:focus {
    background-color: var(--gray-96);
  }

  &.active {
    background-color: var(--info-light);
    color: var(--link-blue);

    &:hover {
      background-color: var(--info-light);
      color: var(--link-blue);
    }

    h4 {
      color: var(--link-blue);
    }
  }

  &.disabled {
    cursor: not-allowed;
    color: var(--gray-80);

    &:hover {
      color: var(--gray-80);
    }
  }

  &:focus:not([data-focus-visible-added]) {
    outline: none;
  }

  .icon {
    margin-right: var(--spacing-50);
  }

  .button-text {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
